"use client"
//  <ChatInterface assistantId="asst_nf073rrkcNnK25TSQYvzG6VP" />
import React, { useState, useEffect, useRef, useContext, forwardRef } from 'react';
import {
  Box,
  Flex,
  Input,
  Button,
  VStack,
  Text,
  Container,
  Card,
  CardBody,
  Switch,
  HStack,
  Heading,
  Image,
  Link,
  Wrap,
  WrapItem,
  IconButton,
  Tooltip,
  Avatar,
  Icon,
  InputGroup,
  InputRightElement,
  Badge,
  Code,
  useColorMode
} from '@chakra-ui/react';
// Remove react-router imports
// import { useNavigate, NavigationType, UNSAFE_NavigationContext } from 'react-router-dom';

import { 
  SunIcon, 
  MoonIcon, 
  ExternalLinkIcon, 
  ChevronUpIcon, 
  ChevronDownIcon,
  ChatIcon,
  DragHandleIcon
} from '@chakra-ui/icons';
// Import React Icons
import { 
  FaRobot, 
  FaUser, 
  FaSun, 
  FaMoon, 
  FaWindowMinimize, 
  FaTimes,
  FaPaperPlane 
} from 'react-icons/fa';
import { MdRefresh } from 'react-icons/md';
// Remove Draggable import
// import Draggable from 'react-draggable';
import Cookies from 'js-cookie';
// Remove Redux imports
// import { useDispatch, useSelector } from "react-redux";

// Import ThemeContext from the correct path and use the correct properties
// Import ThemeContext instead of useColorMode
// import { useTheme } from '../../contexts/ThemeContext';

// ... (keep generateSessionId and other utility functions) ...
const generateSessionId = () => {
  // Generate timestamp part
  const timestamp = Date.now().toString(36);
  
  // Generate unique random parts
  const randomPart1 = Math.random().toString(36).substring(2, 10);
  const randomPart2 = crypto.getRandomValues(new Uint32Array(1))[0].toString(36);
  
  // Use performance.now() for microsecond precision
  const microTime = Math.floor(performance.now() * 1000).toString(36);
  
  // Combine all parts
  const sessionId = `${timestamp}-${randomPart1}-${randomPart2}-${microTime}`;
  
  return sessionId;
};
const MessageContent = ({ content, role, isDarkMode }) => {
  if (role === "tool") {
    return <Code p={2} fontSize="sm" whiteSpace="pre-wrap" width="100%" borderRadius="md" bg={isDarkMode ? "gray.800" : "gray.100"}>{content}</Code>;
  }

  // Add syntax highlighting to code blocks
  const formattedContent = content.replace(
    /```(.+?)\n([\s\S]+?)```/g,
    (match, language, code) => {
      return `<pre class="code-block"><div class="code-header">${language}</div><code>${code}</code></pre>`;
    }
  );

  return (
    <Box
      sx={{
        a: {
          color: isDarkMode ? "blue.300" : "blue.600",
          textDecoration: "none",
          _hover: { textDecoration: "underline" }
        },
        ul: { pl: 4, my: 2 },
        ol: { pl: 4, my: 2 },
        li: { mb: 1 },
        p: { mb: 2 },
        h3: { fontWeight: "bold", mb: 2, mt: 3, fontSize: "md" },
        h4: { fontWeight: "bold", mb: 2, mt: 3, fontSize: "sm" },
        "pre.code-block": {
          bg: isDarkMode ? "gray.800" : "gray.100",
          p: 3,
          borderRadius: "md",
          overflow: "auto",
          my: 2,
          border: "1px solid",
          borderColor: isDarkMode ? "gray.700" : "gray.200",
        },
        ".code-header": {
          fontFamily: "monospace",
          fontSize: "xs",
          color: isDarkMode ? "gray.400" : "gray.500",
          mb: 1
        },
        code: {
          fontFamily: "monospace",
          whiteSpace: "pre-wrap",
          display: "block"
        },
        "p code": {
          fontFamily: "monospace",
          bg: isDarkMode ? "gray.700" : "gray.100",
          px: 1,
          borderRadius: "sm",
          fontSize: "sm",
          display: "inline"
        },
        table: {
          width: "100%",
          my: 2,
          borderCollapse: "collapse"
        },
        th: {
          textAlign: "left",
          p: 1,
          fontWeight: "bold",
          borderBottom: "1px solid",
          borderColor: isDarkMode ? "gray.700" : "gray.200"
        },
        td: {
          p: 1,
          borderBottom: "1px solid",
          borderColor: isDarkMode ? "gray.700" : "gray.200"
        }
      }}
    >
      <div dangerouslySetInnerHTML={{ __html: formattedContent }} />
    </Box>
  );
};

const Message = ({ message, isDarkMode }) => {
  const isUser = message.role === "user";
  const bgColor = isUser 
    ? (isDarkMode ? "blue.700" : "blue.500") 
    : (isDarkMode ? "gray.700" : "gray.100");
  const textColor = isUser 
    ? "white" 
    : (isDarkMode ? "gray.100" : "gray.800");

  return (
    <Flex
      direction="column"
      alignItems={isUser ? "flex-end" : "flex-start"}
      mb={3}
    >
      <Flex
        alignItems="flex-start"
        maxWidth="85%"
      >
        {!isUser && (
          <Box 
            mr={2} 
            mt={1}
            p={1.5}
            borderRadius="full"
            bg={isDarkMode ? "blue.700" : "blue.100"}
          >
            <Icon 
              as={FaRobot} 
              color={isDarkMode ? "white" : "blue.600"} 
              boxSize="0.8rem" 
            />
          </Box>
        )}
        <Box
          p={3}
          bg={bgColor}
          color={textColor}
          borderRadius={isUser ? "2xl 2xl 0 2xl" : "2xl 2xl 2xl 0"}
          boxShadow={`0 1px 2px ${isDarkMode ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.1)'}`}
          fontSize="sm"
          maxWidth="100%"
          width={message.role === "tool" ? "100%" : "auto"}
          transition="all 0.2s"
          animation={`fadeIn 0.3s ease-out`}
          sx={{
            "@keyframes fadeIn": {
              "0%": { opacity: 0, transform: "translateY(5px)" },
              "100%": { opacity: 1, transform: "translateY(0)" }
            }
          }}
        >
          <MessageContent content={message.content} role={message.role} isDarkMode={isDarkMode} />
        </Box>
        {isUser && (
          <Box 
            ml={2} 
            mt={1}
            p={1.5}
            borderRadius="full"
            bg={isDarkMode ? "blue.700" : "blue.500"}
          >
            <Icon 
              as={FaUser} 
              color="white" 
              boxSize="0.8rem" 
            />
          </Box>
        )}
      </Flex>
      <Text
        fontSize="xs"
        color={isDarkMode ? "gray.500" : "gray.500"}
        mt={1}
        mb={2}
        ml={isUser ? 0 : 8}
        mr={isUser ? 8 : 0}
      >
        {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </Text>
    </Flex>
  );
};

const ChatInterface = ({ assistantId }) => {
  // Use Chakra UI's built-in color mode
  const { colorMode, toggleColorMode } = useColorMode();
  const isDarkMode = colorMode === 'dark';

  //const base_url = "https://b1ab9cf0-3155-490f-b5c5-8f7aca34dd82.clouding.host/";
  const base_url = "https://f2895945-7367-4db4-854a-634af23d868f.clouding.host/";
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [thread_id, setThread_id] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isMinimized, setIsMinimized] = useState(true);
  const [chatHeight, setChatHeight] = useState(600);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const messagesEndRef = useRef(null);
  const resizeRef = useRef(null);

  // Use hardcoded colors instead of theme tokens for gradients
  const userMessageBg = isDarkMode 
    ? "#4A5568" // dark gray
    : "#3182CE"; // blue
    
  const assistantBgColor = isDarkMode 
    ? "#2D3748" // dark gray
    : "#EDF2F7"; // light gray

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };
  
  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };
  
  const handleDrag = (e, ui) => {
    setPosition({ x: ui.x, y: ui.y });
  };
  
  const handleResize = (e) => {
    // Prevent default behavior to avoid text selection during resize
    e.preventDefault();
    
    // Add resize event listener
    const startY = e.clientY;
    const startHeight = chatHeight;
    
    const doDrag = (moveEvent) => {
      // Calculate new height
      const newHeight = startHeight + startY - moveEvent.clientY;
      // Set minimum height
      if (newHeight >= 300) {
        setChatHeight(newHeight);
      }
    };
    
    const stopDrag = () => {
      document.removeEventListener('mousemove', doDrag);
      document.removeEventListener('mouseup', stopDrag);
    };
    
    document.addEventListener('mousemove', doDrag);
    document.addEventListener('mouseup', stopDrag);
  };
  
  // Initialize session on component mount
  useEffect(() => {
    let currentSessionId = Cookies.get('chat_session_id');
    let currentThreadId = Cookies.get('currentThreadId');
    if (!currentSessionId) {
      currentSessionId = generateSessionId();
      Cookies.set('chat_session_id', currentSessionId, { expires: 7 }); // Expires in 7 days
    }
    setSessionId(currentSessionId);
  }, []);

  // Fetch messages when session is initialized
  useEffect(() => {
    if (thread_id != false) {
      fetchConversationHistory();
    }
  }, [sessionId, assistantId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchConversationHistory = async () => {
    if (!sessionId) return;

    try {
      const response = await fetch(`/api/chat/history`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          assistant_id: assistantId,
          thread_id: thread_id,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch history');
      }

      const data = await response.json();
      if (data.messages) {
        setMessages(data.messages.map(msg => ({
          ...msg,
          timestamp: new Date(msg.timestamp).toISOString()
        })));
      }
    } catch (error) {
      console.error('Error fetching conversation history:', error);
    }
  };

const sendMessage_stream = async (message) => {
  if (!sessionId) return;
  try {
    setIsLoading(true);
    const newUserMessage = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, newUserMessage]);

    // Create POST request body with thread_id if available
    const requestBody = JSON.stringify({
      session_id: sessionId,
      assistant_id: assistantId,
      message: message,
      thread_id: thread_id || null,
    });

    // Create response placeholder
    const responseMessage = {
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, responseMessage]);

    // Initialize fetch with streaming
    const response = await fetch(base_url+'api/chat/stream', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: requestBody,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Create reader for streaming
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let navigationCommandDetected = false; // Flag to track if navigation command has been detected

    while (true) {
      const { done, value } = await reader.read();
      
      if (done) {
        console.log('Stream complete');
        break;
      }

      // Decode chunk and add to buffer
      buffer += decoder.decode(value, { stream: true });

      // Process complete SSE messages
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6); // Remove 'data: ' prefix
          
          try {
            const parsedData = JSON.parse(data);
            
            if (parsedData.error) {
              console.error('Error:', parsedData.error);
              // Update message with error
              setMessages(prev => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];
                if (lastMessage.role === 'assistant') {
                  lastMessage.content = JSON.stringify({
                    message: "An error occurred. Please try again.",
                    error: true
                  });
                }
                return newMessages;
              });
              break;
            }

            // Handle response for both processing and complete status
            if ((parsedData.status === 'processing' || parsedData.status === 'complete') && parsedData.response) {
              // Get the response text, handling both JSON and plain text
              let responseText = parsedData.response;
              try {
                const parsedJson = JSON.parse(parsedData.response);
                if (parsedJson && parsedJson.message) {
                  responseText = parsedJson.message;
                }
              } catch (e) {
                // Not JSON, use as is
              }
              
              // Check for navigation commands if not already detected
              if (!navigationCommandDetected) {
                const hasNavigation = handleNavigationCommands(
                  responseText,
                  (newItems) => {
                    // This is a setter function for wishlist items
                    // dispatch({ type: 'SET_WISHLIST_ITEMS', payload: newItems });
                    // You might need to adjust this based on your actual state management
                  }
                );
                
                // If navigation command found, set the flag
                if (hasNavigation) {
                  navigationCommandDetected = true;
                  console.log('Navigation command detected and executed during streaming');
                }
              }
              
              // Update message with response
              setMessages(prev => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];
                if (lastMessage.role === 'assistant') {
                  lastMessage.content = parsedData.response;
                }
                return newMessages;
              });
              
              // Store thread_id if provided
              if (parsedData.thread_id) {
                setThread_id(parsedData.thread_id);
              }
            } else if (parsedData.response) {
              // For backward compatibility with the old format
              // Get the response text, handling both JSON and plain text
              let responseText = parsedData.response;
              try {
                const parsedJson = JSON.parse(parsedData.response);
                if (parsedJson && parsedJson.message) {
                  responseText = parsedJson.message;
                }
              } catch (e) {
                // Not JSON, use as is
              }
              
              // Check for navigation commands if not already detected
              if (!navigationCommandDetected) {
                const hasNavigation = handleNavigationCommands(
                  responseText,
                  (newItems) => {
                    // dispatch({ type: 'SET_WISHLIST_ITEMS', payload: newItems });
                  }
                );
                
                // If navigation command found, set the flag
                if (hasNavigation) {
                  navigationCommandDetected = true;
                  console.log('Navigation command detected and executed during streaming');
                }
              }
              
              // Update message with response
              setMessages(prev => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];
                if (lastMessage.role === 'assistant') {
                  lastMessage.content = parsedData.response;
                }
                return newMessages;
              });
            }
          } catch (e) {
            console.error('Error parsing SSE data:', e);
          }
        }
      }
    }

  } catch (error) {
    console.error('Error sending message:', error);
    // Add error message to chat
    setMessages(prev => {
      const newMessages = [...prev];
      const lastMessage = newMessages[newMessages.length - 1];
      if (lastMessage.role === 'assistant') {
        lastMessage.content = JSON.stringify({
          message: "Sorry, there was an error processing your request. Please try again.",
          error: true
        });
      }
      return newMessages;
    });
  } finally {
    setIsLoading(false);
  }
};
// Parse and handle navigation commands from AI responses
const handleNavigationCommands = (response, dispatch) => {
  // Check if response contains navigation commands
  const navigateRegex = /<navigate:\/([^>]+)>/;
  const searchRegex = /<busca:\/([^>]+)>/;
  const newComboRegex = /<action:\/newcombo\/([^>]+)>/;
  const compareRegex = /<action:\/add_compare\/([^>]+)>/;
  const wishlistRegex = /<action:\/add_wishlist\/([^>]+)>/;
  
  // Log navigation requests instead of executing them
  
  // Check for newcombo command
  const newComboMatch = response.match(newComboRegex);
  if (newComboMatch && newComboMatch[1]) {
    const comboInfo = newComboMatch[1];
    console.log(`[Navigation Disabled] Would create new combo: ${comboInfo}`);
    return true;
  }
  
  // Check for add_compare command
  const compareMatch = response.match(compareRegex);
  if (compareMatch && compareMatch[1]) {
    const productIdsStr = compareMatch[1];
    console.log(`[Navigation Disabled] Would add products to compare: ${productIdsStr}`);
    return true;
  }
  
  // Check for add_wishlist command
  const wishlistMatch = response.match(wishlistRegex);
  if (wishlistMatch && wishlistMatch[1]) {
    const productId = wishlistMatch[1].trim();
    console.log(`[Navigation Disabled] Would add product to wishlist: ${productId}`);
    return true;
  }
  
  // Check for navigation command
  const navigateMatch = response.match(navigateRegex);
  if (navigateMatch && navigateMatch[1]) {
    const path = navigateMatch[1];
    console.log(`[Navigation Disabled] Would navigate to: /${path}`);
    return true;
  }
  
  // Check for search command
  const searchMatch = response.match(searchRegex);
  if (searchMatch && searchMatch[1]) {
    const searchTerm = searchMatch[1];
    console.log(`[Navigation Disabled] Would search for: ${searchTerm}`);
    return true;
  }
  
  // Check for legacy 'navegar' command for backward compatibility
  if (response.includes('navegar')) {
    console.log('[Navigation Disabled] Would navigate to: /pack/pizzeria');
    return true;
  }
  
  return false; // No navigation commands found
};
// Function to extract plain text content by removing navigation commands
const cleanResponseText = (response) => {
  // Remove all navigation commands
  return response
    .replace(/<navigate:\/[^>]+>/g, '')
    .replace(/<busca:\/[^>]+>/g, '')
    .replace(/<action:\/newcombo\/[^>]+>/g, '')
    .trim();
};
const sendMessage = (message) => {
  if (!sessionId) return;

  setIsLoading(true);

  // Add user message
  const newUserMessage = {
    role: 'user',
    content: message,
    timestamp: new Date().toISOString(),
  };
  setMessages(prev => [...prev, newUserMessage]);
  fetch('https://b1ab9cf0-3155-490f-b5c5-8f7aca34dd82.clouding.host/api/chat/message/v2', {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'omit',  // Don't send cookies
    body: JSON.stringify({
      message: message,
      assistant_id: assistantId,
      session_id: sessionId
    })
  })
    .then(res => {
      if (!res.ok) throw new Error(String(res.status));
      return res.json();
    })
    .then(data => {
  console.log('Got response:', data);
  
  // Check if data.response exists and is a string
  if (data && data.response) {
    // Process the response for navigation commands
    const hasNavigationCommand = handleNavigationCommands(
      data.response, 
      (newItems) => {
        // This is a setter function for wishlist items
        // dispatch({ type: 'SET_WISHLIST_ITEMS', payload: newItems });
        // You might need to adjust this based on your actual state management
      }
    );
    
    // Clean the response text (remove navigation commands)
    const cleanedResponse = cleanResponseText(data.response);
    
    // Create the assistant message with cleaned text
    const assistantMessage = {
      role: 'assistant',
      content: cleanedResponse,
      timestamp: new Date().toISOString(),
      //provider: //provider // Include the //provider information
    };
    
    // Add the message to the chat
    setMessages(prev => [...prev, assistantMessage]);
    
    // Store thread_id if available
    if (data.thread_id) {
      setThread_id(data.thread_id);
    }
    
    // If this was a navigation command, you might want to log it
    if (hasNavigationCommand) {
      console.log('Navigation command executed from response');
    }
  }
}).catch(error => {
      console.error('Error:', error);
      const errorMessage = {
        role: 'assistant',
        content: JSON.stringify({
          message: `Error: ${error.message}. Please try again.`,
          error: true
        }),
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    })
    .finally(() => {
      setIsLoading(false);
    });
};

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputMessage.trim()) {
      sendMessage_stream(inputMessage);
      setInputMessage("");
      
    }
  };

  // Define missing functions
  const clearMessages = () => {
    setMessages([]);
    setThread_id(false);
  };

  const onClose = () => {
    setIsMinimized(true);
    // You might want additional cleanup logic here
  };

  // If minimized, show only a chat button in the corner with a fixed position
  if (isMinimized) {
    return (
      <Box 
        position="fixed" 
        bottom="20px" 
        right="20px" 
        zIndex={9999}
        sx={{
          "@keyframes pulse": {
            "0%": { boxShadow: "0 0 0 0 rgba(66, 153, 225, 0.7)" },
            "70%": { boxShadow: "0 0 0 15px rgba(66, 153, 225, 0)" },
            "100%": { boxShadow: "0 0 0 0 rgba(66, 153, 225, 0)" }
          },
          animation: "pulse 2s infinite"
        }}
      >
        <IconButton
          icon={<ChatIcon boxSize="20px" />}
          colorScheme="blue"
          size="lg"
          borderRadius="full"
          boxShadow="0 4px 12px rgba(0, 0, 0, 0.15)"
          bg="#4299E1"
          width="60px"
          height="60px"
          onClick={toggleMinimize}
          _hover={{
            bg: "#3182CE",
            transform: 'scale(1.05)',
            boxShadow: "0 6px 16px rgba(0, 0, 0, 0.2)"
          }}
          transition="all 0.3s ease"
          aria-label="Open chat"
        />
      </Box>
    );
  }

  return (
    <Flex
      direction="column"
      position="fixed"
      bottom={0}
      right="20px"
      width={isMinimized ? "auto" : "380px"}
      maxWidth="90vw"
      height={isMinimized ? "auto" : "600px"}
      maxHeight="90vh"
      boxShadow="0 8px 32px rgba(0, 0, 0, 0.15)"
      borderRadius="2xl"
      overflow="hidden"
      zIndex={999}
      transition="all 0.3s ease"
    >
      {isMinimized ? (
        <Button
          onClick={() => setIsMinimized(false)}
          borderRadius="full"
          size="lg"
          colorScheme="blue"
          boxShadow="lg"
          p={4}
          height="60px"
          width="60px"
          color="white"
          _hover={{ 
            transform: "scale(1.05)",
            boxShadow: "0 8px 25px rgba(66, 153, 225, 0.4)" 
          }}
          _active={{ 
            transform: "scale(0.95)",
            boxShadow: "0 4px 15px rgba(66, 153, 225, 0.3)" 
          }}
          transition="all 0.2s"
          sx={{
            "@keyframes pulse": {
              "0%": { boxShadow: "0 0 0 0 rgba(66, 153, 225, 0.4)" },
              "70%": { boxShadow: "0 0 0 15px rgba(66, 153, 225, 0)" },
              "100%": { boxShadow: "0 0 0 0 rgba(66, 153, 225, 0)" }
            },
            animation: "pulse 2s infinite"
          }}
        >
          <Icon as={FaRobot} boxSize="1.5rem" />
        </Button>
      ) : (
        <Box
          height="100%"
          backgroundColor={isDarkMode ? "gray.800" : "white"}
          border="1px solid"
          borderColor={isDarkMode ? "gray.700" : "gray.200"}
          borderRadius="2xl"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <Flex
            p={4}
            alignItems="center"
            borderBottom="1px solid"
            borderColor={isDarkMode ? "gray.700" : "gray.200"}
            bg={isDarkMode ? "gray.800" : "blue.50"}
            justifyContent="space-between"
          >
            <Flex alignItems="center">
              <Box 
                mr={3}
                p={2}
                borderRadius="full"
                bg={isDarkMode ? "blue.700" : "white"}
                boxShadow="0 2px 8px rgba(0, 0, 0, 0.1)"
              >
                <Icon 
                  as={FaRobot} 
                  color={isDarkMode ? "white" : "blue.500"} 
                  boxSize="1.2rem" 
                />
              </Box>
              <Box>
                <Text fontWeight="bold" fontSize="md" color={isDarkMode ? "white" : "gray.700"}>
                  Notto Assistant
                </Text>
                <Text fontSize="xs" color={isDarkMode ? "gray.400" : "gray.500"}>
                  How can I help you today?
                </Text>
              </Box>
            </Flex>
            <Flex>
              <Tooltip label="New Conversation" placement="top">
                <IconButton
                  size="sm"
                  icon={<Icon as={MdRefresh} />}
                  aria-label="New Conversation"
                  variant="ghost"
                  onClick={clearMessages}
                  colorScheme={isDarkMode ? "blue" : "blue"}
                  _hover={{ bg: isDarkMode ? "blue.700" : "blue.50" }}
                  borderRadius="full"
                />
              </Tooltip>
              <Tooltip label="Toggle Dark Mode" placement="top">
                <IconButton
                  size="sm"
                  icon={<Icon as={isDarkMode ? FaSun : FaMoon} />}
                  aria-label="Toggle Dark Mode"
                  variant="ghost"
                  onClick={toggleColorMode}
                  colorScheme={isDarkMode ? "blue" : "blue"}
                  _hover={{ bg: isDarkMode ? "blue.700" : "blue.50" }}
                  borderRadius="full"
                  ml={2}
                />
              </Tooltip>
              <Tooltip label="Minimize" placement="top">
                <IconButton
                  size="sm"
                  icon={<Icon as={FaWindowMinimize} />}
                  aria-label="Minimize"
                  variant="ghost"
                  onClick={() => setIsMinimized(true)}
                  colorScheme={isDarkMode ? "blue" : "blue"}
                  _hover={{ bg: isDarkMode ? "blue.700" : "blue.50" }}
                  borderRadius="full"
                  ml={2}
                />
              </Tooltip>
              <Tooltip label="Close" placement="top">
                <IconButton
                  size="sm"
                  icon={<Icon as={FaTimes} />}
                  aria-label="Close"
                  variant="ghost"
                  onClick={onClose}
                  colorScheme={isDarkMode ? "blue" : "blue"}
                  _hover={{ bg: isDarkMode ? "blue.700" : "blue.50" }}
                  borderRadius="full"
                  ml={2}
                />
              </Tooltip>
            </Flex>
          </Flex>

          <Box
            flex="1"
            overflowY="auto"
            p={4}
            bg={isDarkMode ? "gray.900" : "gray.50"}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: isDarkMode ? "#2D3748" : "#EDF2F7",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb": {
                background: isDarkMode ? "#4A5568" : "#CBD5E0",
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: isDarkMode ? "#718096" : "#A0AEC0",
              },
            }}
            ref={messagesEndRef}
          >
            {messages.length === 0 ? (
              <Flex 
                direction="column" 
                align="center" 
                justify="center" 
                height="100%" 
                color={isDarkMode ? "gray.500" : "gray.500"}
                textAlign="center"
                p={6}
              >
                <Box 
                  mb={6}
                  p={4}
                  borderRadius="full"
                  bg={isDarkMode ? "blue.900" : "blue.50"}
                  boxShadow="0 4px 20px rgba(0, 0, 0, 0.1)"
                >
                  <Icon as={FaRobot} boxSize="2rem" color={isDarkMode ? "blue.300" : "blue.500"} />
                </Box>
                <Text fontSize="md" mb={3} fontWeight="bold" color={isDarkMode ? "gray.300" : "gray.700"}>
                  Welcome to Notto Assistant!
                </Text>
                <Text fontSize="sm" mb={4} maxWidth="80%" color={isDarkMode ? "gray.400" : "gray.600"}>
                  I can help you with setting up your bot, connecting providers, and using Notto effectively.
                </Text>
                <Flex flexWrap="wrap" justifyContent="center" maxWidth="90%">
                  {["How do I create a bot?", "What providers are supported?", "How to connect to Slack?"].map((suggestion) => (
                    <Badge
                      key={suggestion}
                      m={1}
                      py={2}
                      px={3}
                      borderRadius="full"
                      colorScheme="blue"
                      variant={isDarkMode ? "solid" : "subtle"}
                      cursor="pointer"
                      _hover={{ transform: "translateY(-2px)", boxShadow: "sm" }}
                      transition="all 0.2s"
                      onClick={() => {
                        setInputMessage(suggestion);
                        setTimeout(() => {
                          const form = document.querySelector("form");
                          if (form) form.dispatchEvent(new Event("submit", { cancelable: true }));
                        }, 100);
                      }}
                    >
                      {suggestion}
                    </Badge>
                  ))}
                </Flex>
              </Flex>
            ) : (
              <VStack spacing={4} align="stretch">
                {messages.map((message, index) => (
                  <Message 
                    key={index} 
                    message={message} 
                    isDarkMode={isDarkMode} 
                  />
                ))}
              </VStack>
            )}
          </Box>

          <Box 
            p={4}
            borderTop="1px solid"
            borderColor={isDarkMode ? "gray.700" : "gray.200"}
            backgroundColor={isDarkMode ? "gray.800" : "white"}
          >
            <form onSubmit={handleSubmit}>
              <Flex>
                <InputGroup size="md">
                  <Input
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    placeholder="Type your message..."
                    size="md"
                    pr="4.5rem"
                    borderRadius="full"
                    _focus={{
                      borderColor: "blue.400",
                      boxShadow: "0 0 0 1px #4299E1",
                    }}
                    backgroundColor={isDarkMode ? "gray.700" : "gray.50"}
                    color={isDarkMode ? "white" : "black"}
                    _placeholder={{ 
                      color: isDarkMode ? "gray.400" : "gray.500" 
                    }}
                    disabled={isLoading}
                    h="45px"
                  />
                  <InputRightElement width="4.5rem" h="45px">
                    <IconButton
                      isLoading={isLoading}
                      type="submit"
                      colorScheme="blue"
                      variant="solid"
                      borderRadius="full"
                      aria-label="Send message"
                      size="sm"
                      icon={<Icon as={FaPaperPlane} />}
                      _hover={{
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(66, 153, 225, 0.3)"
                      }}
                      transition="all 0.2s"
                      mr={1}
                    />
                  </InputRightElement>
                </InputGroup>
              </Flex>
            </form>
          </Box>
        </Box>
      )}
    </Flex>
  );
};

export default ChatInterface;